/**
 * Sprint 99 — Pure toyyibPay fee estimator.
 *
 * toyyibPay charges a flat / percentage fee per transaction depending on
 * the payment channel the customer picks at their hosted page:
 *
 *   - FPX (online banking)         : RM 1.00 flat
 *   - Credit / debit card          : 2.5% + RM 1.00 (capped at RM 50)
 *   - DuitNow QR + e-wallets       : ~1.5% (toyyibPay does NOT directly
 *                                   route e-wallets — fee varies by aggregator;
 *                                   default to FPX-equivalent for estimation)
 *
 * Per [[project-zero-cut-principle]] this fee is paid by the customer,
 * not the operator. We add it to `totalChargedSen` at intent-creation
 * time so the customer sees `Wash RM 25.00 + Payment fee RM 1.00 = RM 26.00`.
 *
 * Real settlement may differ slightly from the estimate — toyyibPay's
 * actual fee table changes from time to time and the channel the
 * customer actually picks isn't known until they pay. Any small delta
 * is reconciled at the bank level, NOT in the operator-visible wallet
 * ledger (operator's `collected` entry always equals their stated
 * price). Sprint 100 wires the reconciliation cron.
 *
 * ADR-0033 — the fee table is no longer a literal buried in the math.
 * `DEFAULT_TOYYIBPAY_FEE_TABLE` below holds the shipped rates and every
 * function here takes an optional `fees` override, so the server can pass
 * the live `platformConfig/moneyPolicy` table (`psp.toyyibpay`) and a PSP
 * rate change becomes a config edit rather than a deploy. The default is
 * deliberately the *current shipped rate*, not zero: a missing config doc
 * is a config problem, never a silent decision to stop charging the fee.
 */

/**
 * The four numbers that describe toyyibPay's pricing. Structural, not a Zod
 * type, so this module keeps zero dependencies — `MoneyPolicySchema`
 * (`../schemas/money-policy.ts`) builds its `psp.toyyibpay` defaults from
 * the constant below, which keeps one source of truth for the literals.
 *
 * The card percentage is basis points (250 = 2.5%) so the stored value is an
 * integer and no float creeps into a money calculation.
 */
export interface ToyyibPayFeeTable {
  /** Flat sen charged on an FPX (online banking) payment. */
  readonly fpxFlatSen: number;
  /** Card percentage in basis points — 250 = 2.5%. */
  readonly cardPctBasisPoints: number;
  /** Flat sen added on top of the card percentage. */
  readonly cardFlatSen: number;
  /** Ceiling on the TOTAL card fee (percentage + flat), in sen. */
  readonly cardCapSen: number;
}

/**
 * toyyibPay's published rates as shipped: FPX RM 1.00 flat; card 2.5% +
 * RM 1.00 capped at RM 50.00. Used whenever no live table is supplied.
 */
export const DEFAULT_TOYYIBPAY_FEE_TABLE: ToyyibPayFeeTable = {
  fpxFlatSen: 100,
  cardPctBasisPoints: 250,
  cardFlatSen: 100,
  cardCapSen: 5_000,
};

/**
 * toyyibPay channel hint we pass in createBill (their `billPaymentChannel`):
 *   '0' = FPX only
 *   '1' = Cards only
 *   '2' = Both
 * We always pick '2' (both) so the customer chooses at the PSP page.
 * For fee estimation we then assume FPX as the conservative LOW estimate
 * — most customers in MY pick FPX over cards. If the customer ends up
 * paying via card, the actual toyyibPay fee will be slightly higher than
 * what we showed; the small gap is Kilat's reconciliation problem.
 */
export type ToyyibPayChannel = 'fpx' | 'card';

export interface ToyyibPayFee {
  readonly feeSen: number;
  /** Human label that appears on the customer's receipt. */
  readonly description: string;
}

/**
 * Compute the customer-visible payment-processing fee for a given
 * amount + channel hint. Returns fee in sen.
 *
 * For UX consistency we ALWAYS quote the FPX rate at intent-creation
 * time — it's the channel most MY customers actually use. The fee
 * shown matches reality for FPX; for card the customer sees a sub-line
 * "May vary slightly for cards" (UI copy lives in the customer-pwa
 * checkout component).
 */
export function estimateToyyibPayFee(
  amountSen: number,
  channel: ToyyibPayChannel = 'fpx',
  fees: ToyyibPayFeeTable = DEFAULT_TOYYIBPAY_FEE_TABLE,
): ToyyibPayFee {
  if (amountSen <= 0) {
    return { feeSen: 0, description: 'No fee' };
  }
  switch (channel) {
    case 'fpx': {
      // Flat regardless of amount (shipped rate: RM 1.00).
      return { feeSen: fees.fpxFlatSen, description: 'FPX payment fee' };
    }
    case 'card': {
      // Percentage (basis points) + flat, capped at the table's ceiling.
      // Integer arithmetic before the divide keeps this exact for any sen
      // amount the platform can realistically charge.
      const pct = Math.ceil((amountSen * fees.cardPctBasisPoints) / 10_000);
      const total = Math.min(fees.cardCapSen, pct + fees.cardFlatSen);
      return { feeSen: total, description: 'Card payment fee' };
    }
  }
}

/**
 * Compute the totalCustomerCharge from operator's price + estimated fee.
 * Pure convenience wrapper used by `createToyyibPayBill` so the intent
 * doc's three fields stay consistent (amount + fee = total).
 */
export interface TotalChargeBreakdown {
  readonly amountSen: number;
  readonly pspFeeSen: number;
  readonly totalSen: number;
  readonly description: string;
}

export function buildCustomerCharge(
  operatorPriceSen: number,
  channel: ToyyibPayChannel = 'fpx',
  fees: ToyyibPayFeeTable = DEFAULT_TOYYIBPAY_FEE_TABLE,
): TotalChargeBreakdown {
  const { feeSen, description } = estimateToyyibPayFee(operatorPriceSen, channel, fees);
  return {
    amountSen: operatorPriceSen,
    pspFeeSen: feeSen,
    totalSen: operatorPriceSen + feeSen,
    description,
  };
}
